<template>
  <div class="profile-page">
    <!-- Header Section -->
    <div class="profile-header mb-2">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h2 class="font-weight-bold text-primary mb-1">
            Profil Entreprise
          </h2>
          <p class="text-muted mb-0">
            Gérez les informations de votre entreprise et vos coordonnées.
          </p>
        </div>
        <div class="d-none d-md-block">
          <feather-icon
            icon="BriefcaseIcon"
            size="48"
            class="text-primary opacity-25"
          />
        </div>
      </div>
    </div>
    <b-tabs v-model="activeTabIndex" pills class="mt-2" @input="onTabChanged">
      <!-- Tab 1: Informations Générales -->
      <b-tab>
        <template #title>
          <feather-icon icon="UserIcon" size="16" class="mr-0 mr-sm-50" />
          <span class="d-none d-sm-inline">Général</span>
        </template>

        <b-card class="profile-card shadow-sm border-0 rounded-lg">
      <!-- form -->
      <validation-observer ref="simpleRules">
        <b-form class="mt-1">
          <!-- Section 1: Informations modifiables -->
          <div class="mb-3">
            <h5 class="font-weight-bold text-primary mb-1">
              <feather-icon icon="Edit3Icon" class="mr-50" />
              Informations de l'entreprise
            </h5>
            <p class="text-muted small mb-2">Ces informations peuvent être modifiées librement.</p>

            <b-row>
              <!-- Company Details -->
            <b-col md="12">
              <b-form-group
                label="Nom entreprise"
                label-for="company-name"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="name"
                  rules="required"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="BriefcaseIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="company-name"
                      v-model="company.name"
                      :state="errors.length > 0 ? false:null"
                      placeholder="Ex: Auto Care Logistics"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

              <b-col md="12">
              <b-form-group
                label="Adresse complète"
                label-for="address"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="address"
                  rules="required"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="MapPinIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="address"
                      v-model="company.address"
                      :state="errors.length > 0 ? false:null"
                      placeholder="Ex: 123 Rue de la Liberté, Tunis"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <b-form-text>Sera utilisée sur vos bons de livraison.</b-form-text>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Téléphone principal"
                label-for="phone1"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="phone1"
                  rules="required|tnPhone"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="PhoneIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="phone1"
                      v-model="company.phone1"
                      :state="errors.length > 0 ? false:null"
                      placeholder="Ex: 26027474"
                      maxlength="8"
                      minlength="8"
                      type="tel"
                      :formatter="formatPhone"
                      autocomplete="off"
                      @keypress="if (!/\d/.test($event.key)) $event.preventDefault()"
                      @input="company.phone1 = company.phone1.replace(/[^\d]/g, '').slice(0,8)"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <b-form-text>Numéro de téléphone principal (8 chiffres).</b-form-text>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Téléphone secondaire (Optionnel)"
                label-for="phone2"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="phone2"
                  :rules="company.phone2 ? 'tnPhone' : ''"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="PhoneIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="phone2"
                      v-model="company.phone2"
                      :state="errors.length > 0 ? false:null"
                      placeholder="Ex: 26027474"
                      maxlength="8"
                      minlength="8"
                      type="tel"
                      :formatter="formatPhone"
                      autocomplete="off"
                      @keypress="if (!/\d/.test($event.key)) $event.preventDefault()"
                      @input="company.phone2 = company.phone2.replace(/[^\d]/g, '').slice(0,8)"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <b-form-text>Numéro de téléphone secondaire (8 chiffres).</b-form-text>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Nom de page Facebook"
                label-for="page-name"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="page_name"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="FacebookIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="page-name"
                      v-model="company.page_name"
                      :state="errors.length > 0 ? false:null"
                      placeholder="Ex: Auto Care"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Site web"
                label-for="website-url"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="website_url"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="GlobeIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="website-url"
                      v-model="company.website_url"
                      :state="errors.length > 0 ? false:null"
                      placeholder="https://www.example.com"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              class="mt-2 d-flex justify-content-end"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="px-4"
                :disabled="isSaving"
                @click="save"
              >
                <b-spinner v-if="isSaving" small class="mr-1" />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  class="mr-1"
                />
                Enregistrer les modifications
              </b-button>
            </b-col>

            </b-row>
          </div>
        </b-form>
      </validation-observer>
    </b-card>
    </b-tab>

    <!-- Tab 2: Mode synchronisation -->
    <b-tab v-if="canManageSyncMode">
      <template #title>
        <feather-icon
          icon="RefreshCwIcon"
          size="16"
          class="mr-0 mr-sm-50"
        />
        <span class="d-none d-sm-inline">Mode synchronisation</span>
      </template>

      <b-card class="profile-card shadow-sm border-0 rounded-lg">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h5 class="font-weight-bold text-primary mb-1">
              <feather-icon
                icon="RefreshCwIcon"
                class="mr-1"
              />
              Mode de synchronisation
            </h5>
            <p class="text-muted mb-0 small">
              Définissez comment le système route les colis vers les sociétés de livraison partenaires.
            </p>
          </div>
        </div>

        <b-alert
          v-if="hasSyncs"
          variant="warning"
          show
          class="mb-3"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            class="mr-50"
          />
          Changer le mode supprimera les configurations de synchronisation existantes.
        </b-alert>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Stratégie de synchronisation active"
              label-for="sync-mode"
            >
              <b-form-select
                id="sync-mode"
                v-model="syncMode"
                :options="syncModeOptions"
                :disabled="isSavingSyncMode"
                @change="saveSyncMode"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            class="d-flex align-items-center"
          >
            <div
              v-if="syncMode"
              class="bg-light rounded p-2 w-100"
            >
              <small class="text-muted">Mode actif :</small>
              <p class="mb-0 font-weight-bold text-primary">
                {{ getSyncModeLabel(syncMode) }}
              </p>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </b-tab>

    <!-- Tab 2: Informations sensibles (uni-field approval workflow) -->
    <b-tab v-if="isSupplier">
      <template #title>
        <feather-icon icon="ShieldIcon" size="16" class="mr-0 mr-sm-50" />
        <span class="d-none d-sm-inline">Informations sensibles</span>
      </template>

      <b-card class="profile-card shadow-sm border-0 rounded-lg">
        <!-- Top-of-tab unified status banner -->
        <div
          v-if="sensitiveStatusSummary.pending.length > 0 || sensitiveStatusSummary.rejected.length > 0"
          class="premium-status-flag"
          :class="{'danger-mode': sensitiveStatusSummary.rejected.length > 0}"
        >
          <div class="flag-content">
            <div class="flag-title">
              <span v-if="sensitiveStatusSummary.pending.length > 0">
                Vous avez une demande en attente d'approbation
              </span>
              <span v-if="sensitiveStatusSummary.pending.length > 0 && sensitiveStatusSummary.rejected.length > 0">
                ·
              </span>
              <span v-if="sensitiveStatusSummary.rejected.length > 0" class="text-danger">
                {{ sensitiveStatusSummary.rejected.length }} demande(s) refusée(s)
              </span>
            </div>
            <div v-if="sensitiveStatusSummary.pending.length > 0">
              <span class="flag-detail-badge">
                {{ sensitiveStatusSummary.pending[0].field_display || sensitiveStatusSummary.pending[0].field_name }}
                → {{ formatSensitiveValue(sensitiveStatusSummary.pending[0].new_value, sensitiveStatusSummary.pending[0].field_name) }}
              </span>
              <div class="flag-description">
                Une seule demande à la fois est autorisée. Annulez celle-ci pour en soumettre une nouvelle, ou attendez la validation de l'administrateur.
              </div>
            </div>
            <div v-if="sensitiveStatusSummary.rejected.length > 0" class="mt-1 small">
              <span
                v-for="req in sensitiveStatusSummary.rejected"
                :key="`r-${req.id}`"
                class="badge badge-light-danger mr-50 mb-25 cursor-pointer"
                @click="openRejectedDetail(req)"
              >
                {{ req.field_display || req.field_name }} — voir le motif
              </span>
            </div>
          </div>
          <b-button
            v-if="sensitiveStatusSummary.pending.length > 0"
            variant="outline-warning"
            size="sm"
            class="flag-action-btn"
            @click="cancelPendingRequest(sensitiveStatusSummary.pending[0].id)"
          >
            <feather-icon icon="XIcon" size="12" class="mr-50" />
            Annuler ma demande
          </b-button>
        </div>

        <b-alert
          :show="sensitiveStatusSummary.pending.length === 0 && sensitiveStatusSummary.rejected.length === 0"
          variant="info"
          class="mb-3"
        >
          <feather-icon icon="InfoIcon" class="mr-50" />
          Les modifications ci-dessous nécessitent une validation de l'administrateur.
        </b-alert>

        <!-- Sub-group: Légal -->
        <div class="mb-3">
          <h5 class="font-weight-bold text-primary mb-1">
            <feather-icon icon="FileTextIcon" class="mr-50" />
            Légal
          </h5>
          <p class="text-muted small mb-2">CIN et matricule fiscale — modification soumise à validation administrateur.</p>

          <b-row>
            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                name="CIN"
                rules="digits:8"
                vid="cin"
              >
                <b-form-group class="mb-2">
                  <template #label>
                    <div class="d-flex align-items-center">
                      <span>CIN</span>
                      <feather-icon icon="LockIcon" size="12" class="text-muted ml-50" v-b-tooltip.hover title="Modification soumise à validation — 8 chiffres exactement" />
                    </div>
                  </template>
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="CreditCardIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="legalDraft.cin"
                      :state="errors.length > 0 ? false : (legalDraft.cin && legalDraft.cin.length === 8 ? true : null)"
                      :disabled="!!pendingRequestByField.cin"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      maxlength="8"
                      placeholder="Ex: 01234567"
                      @input="onCinInput"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <div v-if="rejectedRequestByField.cin" class="mt-50">
                    <b-badge
                      variant="light-danger"
                      class="cursor-pointer p-50"
                      @click="openRejectedDetail(rejectedRequestByField.cin)"
                    >
                      <feather-icon icon="XCircleIcon" class="mr-25" size="12"/> Demande refusée — voir le motif
                    </b-badge>
                  </div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <b-form-group class="mb-2">
                <template #label>
                  <div class="d-flex align-items-center">
                    <span>Matricule fiscale</span>
                    <feather-icon icon="LockIcon" size="12" class="text-muted ml-50" v-b-tooltip.hover title="Modification soumise à validation" />
                  </div>
                </template>
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="FileTextIcon" />
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="legalDraft.tax_identification_number"
                    :disabled="!!pendingRequestByField.tax_identification_number"
                    placeholder="Ex: 1234567/A/B/C/000"
                  />
                </b-input-group>
                <div v-if="rejectedRequestByField.tax_identification_number" class="mt-50">
                  <b-badge
                    variant="light-danger"
                    class="cursor-pointer p-50"
                    @click="openRejectedDetail(rejectedRequestByField.tax_identification_number)"
                  >
                    <feather-icon icon="XCircleIcon" class="mr-25" size="12"/> Demande refusée — voir le motif
                  </b-badge>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <hr class="my-3 border-light" />

        <!-- Sub-group: Paiement -->
        <div class="mb-2">
          <h5 class="font-weight-bold text-primary mb-1">
            <feather-icon icon="CreditCardIcon" class="mr-50" />
            Paiement
          </h5>
          <p class="text-muted small mb-2">
            Type de paiement, RIB et jours de paiement préférés — modification soumise à validation administrateur.
          </p>

          <b-row>
            <b-col md="6">
              <b-form-group class="mb-2">
                <template #label>
                  <div class="d-flex align-items-center">
                    <span>Type de paiement</span>
                    <feather-icon icon="LockIcon" size="12" class="text-muted ml-50" v-b-tooltip.hover title="Modification soumise à validation" />
                  </div>
                </template>
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="DollarSignIcon" />
                  </b-input-group-prepend>
                  <b-form-select
                    v-model="paymentDraft.payment_type"
                    :options="[{ value: 'espece', text: 'Espèce' }, { value: 'virement', text: 'Virement' }]"
                    :disabled="!!pendingRequestByField.payment_type"
                  />
                </b-input-group>
                <div v-if="rejectedRequestByField.payment_type" class="mt-50">
                  <b-badge
                    variant="light-danger"
                    class="cursor-pointer p-50"
                    @click="openRejectedDetail(rejectedRequestByField.payment_type)"
                  >
                    <feather-icon icon="XCircleIcon" class="mr-25" size="12"/> Demande refusée — voir le motif
                  </b-badge>
                </div>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                name="RIB"
                rules="digits:20"
                vid="rib"
              >
                <b-form-group class="mb-2">
                  <template #label>
                    <div class="d-flex align-items-center">
                      <span>RIB</span>
                      <feather-icon icon="LockIcon" size="12" class="text-muted ml-50" v-b-tooltip.hover title="Modification soumise à validation — 20 chiffres exactement" />
                    </div>
                  </template>
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="HashIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="paymentDraft.rib"
                      :state="errors.length > 0 ? false : (paymentDraft.rib && paymentDraft.rib.length === 20 ? true : null)"
                      :disabled="!!pendingRequestByField.rib"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      maxlength="20"
                      placeholder="Ex: 01234567890123456789"
                      @input="onRibInput"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <div v-if="rejectedRequestByField.rib" class="mt-50">
                    <b-badge
                      variant="light-danger"
                      class="cursor-pointer p-50"
                      @click="openRejectedDetail(rejectedRequestByField.rib)"
                    >
                      <feather-icon icon="XCircleIcon" class="mr-25" size="12"/> Demande refusée — voir le motif
                    </b-badge>
                  </div>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <div class="mt-3 mb-2">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div class="d-flex align-items-center">
                <h6 class="font-weight-bold mb-0">Jours de paiement préférés</h6>
                <feather-icon icon="LockIcon" size="12" class="text-muted ml-50" v-b-tooltip.hover title="Modification soumise à validation" />
              </div>
              <div v-if="rejectedRequestByField.invoice_generation_days" class="d-flex">
                <b-badge
                  variant="light-danger"
                  class="cursor-pointer p-50"
                  @click="openRejectedDetail(rejectedRequestByField.invoice_generation_days)"
                >
                  <feather-icon icon="XCircleIcon" class="mr-25" size="12" /> Demande refusée — voir le motif
                </b-badge>
              </div>
            </div>

            <div class="premium-checkbox-group mt-1">
              <div v-if="!paymentDraft.invoice_generation_days.length" class="text-muted small mb-1 w-100">
                <feather-icon icon="InfoIcon" size="14" class="mr-25"/> Aucun jour préféré n'est configuré.
              </div>
              <b-form-checkbox-group
                v-model="paymentDraft.invoice_generation_days"
                :options="paymentDayOptions"
                :disabled="!!pendingRequestByField.invoice_generation_days"
                class="custom-checkbox-grid"
              />
            </div>
          </div>
        </div>

        <b-form-group class="d-flex justify-content-end mt-3">
          <span id="submit-sensitive-btn-wrapper">
            <b-button
              variant="primary"
              class="px-4 submit-sensitive-btn"
              :class="{ 'not-allowed': hasAnyPendingSensitiveRequest }"
              :disabled="hasAnyPendingSensitiveRequest"
              aria-label="Soumettre les modifications sensibles pour validation"
              @click="openBatchRequestModal()"
            >
              <feather-icon icon="SendIcon" class="mr-50" />
              <span class="ml-1">Soumettre les modifications</span>
            </b-button>
            <b-tooltip
              v-if="hasAnyPendingSensitiveRequest"
              target="submit-sensitive-btn-wrapper"
              triggers="hover focus"
              placement="top"
            >
              Vous avez déjà une demande en attente. Annulez-la pour soumettre une nouvelle modification.
            </b-tooltip>
          </span>
        </b-form-group>

        <!-- Recent change requests history mini-list (last 5) -->
        <div class="mt-4">
          <h6 class="font-weight-bold mb-2">
            <feather-icon icon="ListIcon" class="mr-50" />
            Demandes envoyées
          </h6>
          <div v-if="!recentChangeRequests.length" class="text-muted small">
            Aucune demande n'a encore été envoyée.
          </div>
          <div v-else class="premium-table-wrapper mt-1">
            <b-table-simple responsive class="mb-0 premium-requests-table" aria-label="Demandes de modification récentes">
              <caption class="sr-only">
                Historique de vos demandes de modification
              </caption>
              <b-thead>
                <b-tr>
                  <b-th>Date</b-th>
                  <b-th>Champ</b-th>
                  <b-th>Demandé</b-th>
                  <b-th>Statut</b-th>
                  <b-th>Motif</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="req in recentChangeRequests" :key="req.id">
                  <b-td class="small">{{ formatDate(req.created_at) }}</b-td>
                  <b-td class="small">{{ req.field_display || req.field_name }}</b-td>
                  <b-td class="small">{{ formatSensitiveValue(req.new_value, req.field_name) }}</b-td>
                  <b-td>
                    <b-badge :variant="getRequestStatusVariant(req.status)" class="p-50">
                      {{ req.status_display || req.status }}
                    </b-badge>
                  </b-td>
                  <b-td>
                    <span
                      v-b-tooltip.hover
                      :title="req.admin_response || ''"
                      class="admin-response-cell small"
                    >{{ req.admin_response || '—' }}</span>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
        </div>
      </b-card>
    </b-tab>

    <!-- Rejected request detail modal -->
    <b-modal
      v-model="rejectedDetailModal.show"
      title="Détails de la demande refusée"
      centered
      hide-footer
    >
      <ul class="list-unstyled mb-0">
        <li class="mb-1">
          <strong>Champ :</strong> {{ rejectedDetailModal.req.field_display || rejectedDetailModal.req.field_name }}
        </li>
        <li class="mb-1">
          <strong>Valeur demandée :</strong>
          {{ formatSensitiveValue(rejectedDetailModal.req.new_value, rejectedDetailModal.req.field_name) }}
        </li>
        <li class="mb-1">
          <strong>Refusée par :</strong> {{ rejectedDetailModal.req.processed_by_display || '—' }}
        </li>
        <li class="mb-1">
          <strong>Date :</strong> {{ rejectedDetailModal.req.processed_at ? formatDate(rejectedDetailModal.req.processed_at) : '—' }}
        </li>
        <li class="mb-1">
          <strong>Motif de l'administrateur :</strong>
          <div class="mt-50 text-muted">
            {{ rejectedDetailModal.req.admin_response || 'Aucun motif fourni.' }}
          </div>
        </li>
      </ul>
      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" @click="rejectedDetailModal.show = false">
          Fermer
        </b-button>
      </div>
    </b-modal>

    <!-- Tab 3: Sécurité -->
    <b-tab>
      <template #title>
        <feather-icon icon="LockIcon" size="16" class="mr-0 mr-sm-50" />
        <span class="d-none d-sm-inline">Sécurité</span>
      </template>

      <!-- Change Password -->
      <b-card class="profile-card shadow-sm border-0 rounded-lg">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 class="font-weight-bold text-primary mb-1">
            <feather-icon
              icon="LockIcon"
              class="mr-1"
            />
            Modifier le mot de passe
          </h5>
          <p class="text-muted mb-0 small">
            Mettez à jour votre mot de passe de connexion.
          </p>
        </div>
      </div>

      <validation-observer ref="passwordRules">
        <b-form @submit.prevent="changePassword">
          <b-row>
            <b-col md="12">
              <b-form-group
                label="Mot de passe actuel"
                label-for="old-password"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Mot de passe actuel"
                  rules="required"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="LockIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="old-password"
                      v-model="password.old_password"
                      type="password"
                      :state="errors.length > 0 ? false:null"
                      placeholder="••••••••"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Nouveau mot de passe"
                label-for="new-password"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Nouveau mot de passe"
                  vid="password"
                  rules="required|password"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="KeyIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="new-password"
                      v-model="password.password"
                      type="password"
                      :state="errors.length > 0 ? false:null"
                      placeholder="••••••••"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group
                label="Confirmer le mot de passe"
                label-for="confirm-password"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Confirmation du mot de passe"
                  rules="required|confirmed:password"
                >
                  <b-input-group class="input-group-merge">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="KeyIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="confirm-password"
                      v-model="password.confirm_password"
                      type="password"
                      :state="errors.length > 0 ? false:null"
                      placeholder="••••••••"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              class="mt-2 d-flex justify-content-end"
            >
              <span id="profile-password-save-btn-wrapper" class="d-inline-block">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  type="submit"
                  :class="{ 'not-allowed': isSubmittingPassword || !canSubmitPassword }"
                  :disabled="isSubmittingPassword || !canSubmitPassword"
                  class="px-4"
                >
                  <b-spinner v-if="isSubmittingPassword" small class="mr-1" />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    class="mr-1"
                  />
                  Mettre à jour le mot de passe
                </b-button>
                <b-tooltip
                  v-if="!canSubmitPassword || isSubmittingPassword"
                  target="profile-password-save-btn-wrapper"
                  triggers="hover focus"
                  placement="top"
                >
                  <template v-if="isSubmittingPassword">
                    Mise à jour en cours...
                  </template>
                  <template v-else-if="!password.old_password">
                    Veuillez saisir votre ancien mot de passe.
                  </template>
                  <template v-else-if="password.password && password.confirm_password && !passwordsMatch">
                    Les mots de passe ne correspondent pas.
                  </template>
                  <template v-else-if="password.password && password.password.length < 8">
                    Le nouveau mot de passe doit comporter au moins 8 caractères.
                  </template>
                  <template v-else-if="!password.password || !password.confirm_password">
                    Veuillez remplir les champs nouveau mot de passe et confirmation.
                  </template>
                  <template v-else>
                    Veuillez remplir correctement tous les champs.
                  </template>
                </b-tooltip>
              </span>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>

    </b-card>

    <div v-if="isSupplier" class="mt-3">
      <request-account-disable />
    </div>
    </b-tab>

    <!-- Tab 3: Mes Tarifs -->
    <b-tab v-if="isSupplier">
      <template #title>
        <feather-icon icon="DollarSignIcon" size="16" class="mr-0 mr-sm-50" />
        <span class="d-none d-sm-inline">Tarifs</span>
      </template>

      <!-- Supplier Pricing -->
      <b-card class="profile-card shadow-sm border-0 rounded-lg">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 class="font-weight-bold text-primary mb-1">
            <feather-icon
              icon="DollarSignIcon"
              class="mr-1"
            />
            Mes tarifs
          </h5>
          <p class="text-muted mb-0 small">
            Consultez vos tarifs de livraison et de retour.
          </p>
        </div>
      </div>

      <div
        v-if="isLoadingPricing"
        class="text-center py-3"
      >
        <b-spinner
          variant="primary"
          small
        />
      </div>

      <div v-else>
        <b-row>
          <b-col md="6" xl="4">
            <div class="premium-metric-card">
              <div class="metric-icon-wrapper">
                <feather-icon icon="TagIcon" size="20" />
              </div>
              <div class="metric-content">
                <small class="metric-label">Mode de tarification</small>
                <h4 class="metric-value">{{ getPricingModeLabel(pricing.pricing_mode) }}</h4>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Fixed pricing -->
        <div v-if="pricing.pricing_mode === 'fixed'">
          <b-row>
            <b-col md="6" xl="4">
              <div class="premium-metric-card">
                <div class="metric-icon-wrapper">
                  <feather-icon icon="TruckIcon" size="20" />
                </div>
                <div class="metric-content">
                  <small class="metric-label">Frais de livraison / colis</small>
                  <h4 class="metric-value">{{ formatPrice(pricing.price_delivery) }}</h4>
                </div>
              </div>
            </b-col>
            <b-col md="6" xl="4">
              <div class="premium-metric-card">
                <div class="metric-icon-wrapper">
                  <feather-icon icon="RotateCcwIcon" size="20" />
                </div>
                <div class="metric-content">
                  <small class="metric-label">Frais de retour / colis</small>
                  <h4 class="metric-value">{{ formatPrice(pricing.price_return) }}</h4>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Weight-based pricing -->
        <div
          v-else-if="pricing.pricing_mode === 'weight'"
          class="mt-3"
        >
          <h6 class="font-weight-bold">
            Tarifs par poids
          </h6>
          <div
            v-if="!weightPriceRows.length"
            class="text-muted small"
          >
            Aucun tarif par poids configuré.
          </div>
          <div v-else class="premium-table-wrapper mt-2">
            <b-table-simple
              responsive
              class="mb-0 premium-requests-table"
            >
              <b-thead>
                <b-tr>
                  <b-th>Poids</b-th>
                  <b-th>Livraison / colis</b-th>
                  <b-th>Retour / colis</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr
                  v-for="(row, index) in weightPriceRows"
                  :key="`wp-${index}`"
                >
                  <b-td>{{ row.label }}</b-td>
                  <b-td>{{ formatPrice(row.price_delivery) }}</b-td>
                  <b-td>{{ formatPrice(row.price_return) }}</b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
        </div>

        <!-- Governorate-based pricing -->
        <div
          v-else-if="pricing.pricing_mode === 'governorate'"
          class="mt-3"
        >
          <h6 class="font-weight-bold">
            Tarifs par gouvernorat
          </h6>
          <div
            v-if="!pricing.governorate_prices || !pricing.governorate_prices.length"
            class="text-muted small"
          >
            Aucun tarif par gouvernorat configuré.
          </div>
          <div v-else class="premium-table-wrapper mt-2">
            <b-table-simple
              responsive
              class="mb-0 premium-requests-table"
            >
              <b-thead>
                <b-tr>
                  <b-th>Gouvernorat</b-th>
                  <b-th>Livraison</b-th>
                  <b-th>Retour</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr
                  v-for="(gp, index) in pricing.governorate_prices"
                  :key="`gp-${index}`"
                >
                  <b-td>{{ gp.governorate_name || gp.governorate || gp.name || '—' }}</b-td>
                  <b-td>{{ formatPrice(gp.price_delivery) }}</b-td>
                  <b-td>{{ formatPrice(gp.return_price || gp.price_return) }}</b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
          <div
            v-if="pricing.price_delivery != null || pricing.price_return != null"
            class="mt-2 text-muted small"
          >
            Tarif par défaut — Livraison : {{ formatPrice(pricing.price_delivery) }} / Retour : {{ formatPrice(pricing.price_return) }}
          </div>
        </div>

        <div class="mt-3 text-muted small">
          En cas de question sur vos tarifs, contactez l'administrateur.
        </div>
        </div>
    </b-card>
    </b-tab>

    <!-- Tab 4: API Token -->
    <b-tab v-if="isSupplier">
      <template #title>
        <feather-icon icon="KeyIcon" size="16" class="mr-0 mr-sm-50" />
        <span class="d-none d-sm-inline">API Token</span>
      </template>

      <!-- API Token -->
      <b-card class="profile-card shadow-sm border-0 rounded-lg">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 class="font-weight-bold text-primary mb-1">
            <feather-icon
              icon="KeyIcon"
              class="mr-1"
            />
            API Token
          </h5>
          <p class="text-muted mb-0 small">
            Utilisez ce token pour lier votre compte à une application externe.
          </p>
        </div>
      </div>

      <div
        v-if="isLoadingToken"
        class="text-center py-3"
      >
        <b-spinner
          variant="primary"
          small
        />
      </div>

      <div v-else-if="tokenError">
        <b-alert
          variant="warning"
          show
          class="mb-0"
        >
          <div class="d-flex align-items-center justify-content-between">
            <span>Impossible de charger le token.</span>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="fetchToken"
            >
              Réessayer
            </b-button>
          </div>
        </b-alert>
      </div>

      <div v-else class="premium-token-container mt-2 mb-3">
        <label for="api-token" class="premium-label">Token d'API</label>
        <b-input-group class="premium-token-input-group mb-2">
          <b-form-input
            id="api-token"
            class="premium-token-input"
            :type="showToken ? 'text' : 'password'"
            :value="token || 'Aucun token disponible'"
            readonly
          />
          <b-input-group-append>
            <b-button
              variant="flat-secondary"
              class="token-action-btn"
              @click="showToken = !showToken"
              v-b-tooltip.hover
              title="Afficher/Masquer"
            >
              <feather-icon :icon="showToken ? 'EyeOffIcon' : 'EyeIcon'" size="16" />
            </b-button>
            <b-button
              variant="flat-secondary"
              class="token-action-btn copy-btn"
              :disabled="!token"
              @click="copyToken"
            >
              <feather-icon
                icon="CopyIcon"
                size="16"
                class="mr-50"
              />
              <span class="font-weight-bold">Copier</span>
            </b-button>
          </b-input-group-append>
        </b-input-group>

        <div class="security-hint text-muted small d-flex align-items-center mt-1">
          <feather-icon icon="ShieldIcon" size="14" class="mr-50 text-warning" />
          <span>Ce token permet d'accéder à vos données. Ne le partagez jamais publiquement.</span>
        </div>
      </div>
      <supplier-api-documentation :token="token" />
    </b-card>
    </b-tab>
    </b-tabs>

    <!-- Enhanced Sync Mode Confirmation Modal -->
    <b-modal
      id="sync-mode-confirm-modal"
      ref="syncConfirmModal"
      centered
      hide-footer
      hide-header
      size="md"
      body-class="p-0"
      content-class="rounded-xl overflow-hidden border-0 shadow-lg"
    >
      <div class="sync-confirm-modal p-4 text-center">
        <div class="d-flex justify-content-center mb-3 pt-2">
          <div class="top-icon-container">
            <div class="top-icon-inner">
              <feather-icon
                icon="Trash2Icon"
                size="22"
                class="text-danger"
              />
            </div>
          </div>
        </div>

        <h3 class="font-weight-bolder text-dark mb-1">
          Suppression des configurations
        </h3>
        <p class="text-secondary px-2 mb-4">
          Cette action est irréversible
        </p>

        <div class="danger-zone-box text-left mb-4">
          <div class="d-flex align-items-start">
            <feather-icon
              icon="AlertTriangleIcon"
              size="20"
              class="text-danger mr-2 mt-25 flex-shrink-0"
            />
            <div>
              <h6 class="text-danger font-weight-bolder mb-1">
                Zone de danger
              </h6>
              <p class="mb-0 text-dark small">
                Changer le mode de synchronisation supprimera <strong>toutes vos configurations</strong> ({{ syncConfigCount }}). Irréversible.
              </p>
            </div>
          </div>
        </div>

        <div class="confirmation-section-new mb-4 text-left">
          <label class="text-uppercase text-secondary x-small font-weight-bolder mb-2 d-block text-center">
            Tapez "SUPPRIMER" pour confirmer
          </label>
          <div class="position-relative">
            <b-form-input
              v-model="confirmationText"
              placeholder="SUPPRIMER"
              class="confirmation-input-new text-center h3 py-3"
              @keyup.enter="confirmSyncModeChange"
            />
            <feather-icon
              v-if="confirmationText !== 'SUPPRIMER'"
              icon="LockIcon"
              size="14"
              class="position-absolute text-light mr-3"
              style="right: 0; top: 50%; transform: translateY(-50%);"
            />
          </div>
        </div>

        <div class="d-flex flex-row justify-content-between">
          <b-button
            variant="light"
            class="flex-grow-1 mr-2 py-2 btn-cancel"
            @click="cancelSyncModeChange"
          >
            Annuler
          </b-button>
          <b-button
            variant="danger"
            class="flex-grow-1 py-2 shadow-danger btn-confirm border-0"
            :disabled="confirmationText !== 'SUPPRIMER' || isSavingSyncMode"
            @click="confirmSyncModeChange"
          >
            <b-spinner
              v-if="isSavingSyncMode"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="Trash2Icon"
              size="16"
              class="mr-50"
            />
            Supprimer
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Change request modal -->
    <b-modal
      v-model="requestModal.show"
      :title="`Demande de modification – ${requestModal.fieldLabel}`"
      hide-footer
      centered
      size="md"
      @hidden="closeRequestModal"
    >
      <div class="mb-2">
        <small class="text-muted">Valeur actuelle</small>
        <p class="mb-0 font-weight-bold">
          {{ requestModal.fieldType === 'checkbox'
            ? (requestModal.currentValue || []).map(v => getPaymentDayLabel(v)).join(', ') || '—'
            : (requestModal.currentValue || '—') }}
        </p>
      </div>

      <div class="mb-2">
        <label class="d-block mb-1">
          Nouvelle valeur
        </label>
        <b-form-input
          v-if="requestModal.fieldType === 'text'"
          v-model="requestModal.newValue"
          :placeholder="`Nouveau ${requestModal.fieldLabel.toLowerCase()}`"
        />
        <select
          v-else-if="requestModal.fieldType === 'select'"
          v-model="requestModal.newValue"
          class="form-control"
        >
          <option
            v-for="opt in requestModal.options"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.text }}
          </option>
        </select>
        <b-form-checkbox-group
          v-else-if="requestModal.fieldType === 'checkbox'"
          v-model="requestModal.newValue"
          :options="requestModal.options"
          stacked
        />
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="closeRequestModal"
        >
          Annuler
        </b-button>
        <b-button
          variant="primary"
          :disabled="requestModal.isSubmitting"
          @click="submitProfileChangeRequest"
        >
          <b-spinner
            v-if="requestModal.isSubmitting"
            small
            class="mr-1"
          />
          Envoyer la demande
        </b-button>
      </div>
    </b-modal>

    <!-- Batch Request Confirmation Modal -->
    <b-modal
      id="batch-request-modal"
      v-model="batchRequestModal.show"
      title="Confirmer la demande de modification"
      centered
    >
      <p>Vous êtes sur le point de soumettre une demande pour les modifications suivantes :</p>
      <b-table-simple striped hover small class="mt-2 border">
        <b-thead>
          <b-tr>
            <b-th>Champ</b-th>
            <b-th>Actuel</b-th>
            <b-th>Nouveau</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="change in batchRequestModal.changes" :key="change.field">
            <b-td>{{ change.label }}</b-td>
            <b-td>{{ change.old }}</b-td>
            <b-td class="font-weight-bold text-primary">{{ change.newLabel || change.new }}</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="batchRequestModal.show = false">Annuler</b-button>
        <b-button variant="primary" :disabled="batchRequestModal.isSubmitting" @click="submitBatchRequests">
          <b-spinner v-if="batchRequestModal.isSubmitting" small class="mr-50" />
          Confirmer la demande
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BForm, BRow, BCol, BFormGroup, BFormInput, BButton, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BSpinner, BTableSimple, BThead, BTbody, BTr, BTh, BTd, BAlert, BFormText, BTabs, BTab, BFormCheckboxGroup, BModal, BBadge, BTooltip,
} from 'bootstrap-vue'

import Ripple from 'vue-ripple-directive'
import storeAuth from '@/store/store'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import RequestAccountDisable from '@/views/supplier/RequestAccountDisable.vue'
import SupplierApiDocumentation from '@/components/SupplierApiDocumentation.vue'

export default {
  components: {
    BCard,
    BForm,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BSpinner,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BAlert,
    BFormText,
    ValidationProvider,
    ValidationObserver,
    BTabs,
    BTab,
    BFormCheckboxGroup,
    BModal,
    BBadge,
    BTooltip,
    RequestAccountDisable,
    SupplierApiDocumentation,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      activeTabIndex: 0,
      legalDraft: { cin: '', tax_identification_number: '' },
      paymentDraft: { payment_type: '', rib: '', invoice_generation_days: [] },
      batchRequestModal: {
        show: false, isSubmitting: false, section: '', changes: [],
      },
      company: {
        name: '',
        address: '',
        phone1: '',
        phone2: '',
        tax_identification_number: '',
        cin: '',
        page_name: '',
        website_url: '',
      },
      currentUserId: null,
      isSaving: false,
      required,
      // Password change
      password: {
        old_password: '',
        password: '',
        confirm_password: '',
      },
      isSubmittingPassword: false,
      // Supplier pricing
      pricing: {
        price_delivery: null,
        price_return: null,
        prices: [],
        weight_price: false,
        governorate_prices: [],
        pricing_mode: 'fixed',
        weights: [],
      },
      isLoadingPricing: false,
      // Synchronization mode configuration
      syncMode: 'governorate',
      originalSyncMode: 'governorate',
      hasSyncs: false,
      isSavingSyncMode: false,
      syncModeOptions: [
        { value: 'supplier', text: 'Par fournisseur' },
        { value: 'governorate', text: 'Par gouvernorat' },
        { value: 'supplier_governorate', text: 'Par fournisseur et gouvernorat' },
      ],
      confirmationText: '',
      pendingSyncMode: null,
      syncConfigCount: 0,
      // API token
      token: '',
      showToken: false,
      isLoadingToken: false,
      tokenError: false,
      // Payment (read-only for supplier)
      paymentType: '',
      rib: '',
      invoiceDays: [],
      // Profile change request workflow
      requestModal: {
        show: false,
        isSubmitting: false,
        field: '',
        fieldLabel: '',
        fieldType: 'text',
        currentValue: null,
        newValue: '',
        options: [],
      },
      profileRequests: [],
      isLoadingRequests: false,
      rejectedDetailModal: { show: false, req: {} },
    }
  },
  computed: {
    passwordsMatch() {
      return (
        this.password.password
        && this.password.confirm_password
        && this.password.password === this.password.confirm_password
      )
    },
    canSubmitPassword() {
      return (
        this.password.old_password
        && this.password.password
        && this.password.confirm_password
        && this.passwordsMatch
        && this.password.password.length >= 8
      )
    },
    isSupplier() {
      const user = storeAuth.state.user || {}
      const role = user.role || localStorage.getItem('role')
      return role === 'supplier'
    },
    canManageSyncMode() {
      const user = storeAuth.state.user || {}
      const role = user.role || localStorage.getItem('role')
      return role === 'admin' || user.is_staff || user.is_superuser
    },
    tabIndexToName() {
      const tabs = ['general', 'security']
      if (this.canManageSyncMode) {
        tabs.splice(1, 0, 'sync-mode')
      }
      if (this.isSupplier) {
        tabs.splice(1, 0, 'sensitive')
        tabs.push('pricing', 'token')
      }
      return tabs
    },
    tabNameToIndex() {
      const map = {}
      this.tabIndexToName.forEach((name, index) => {
        map[name] = index
      })
      return map
    },
    weightPriceRows() {
      const rows = []
      const entries = Array.isArray(this.pricing.prices) ? this.pricing.prices : []
      entries.forEach(entry => {
        if (!entry || typeof entry !== 'object') return
        const weight = entry.weight || {}
        let label = '—'
        if (weight.name) {
          const weightPart = weight.weight ? `(${weight.weight} ${weight.unit || ''})`.trim() : ''
          label = `${weight.name} ${weightPart}`.trim()
        } else if (weight.weight) {
          label = `${weight.weight} ${weight.unit || ''}`.trim()
        }
        rows.push({
          label,
          price_delivery: entry.price_delivery,
          price_return: entry.price_return,
        })
      })
      return rows
    },
    paymentDayOptions() {
      return [
        { value: 'monday', text: 'Lundi' },
        { value: 'tuesday', text: 'Mardi' },
        { value: 'wednesday', text: 'Mercredi' },
        { value: 'thursday', text: 'Jeudi' },
        { value: 'friday', text: 'Vendredi' },
        { value: 'saturday', text: 'Samedi' },
        { value: 'sunday', text: 'Dimanche' },
      ]
    },

    rejectedRequestByField() {
      const map = {}
      this.profileRequests.forEach(req => {
        if (req.status === 'rejected' && req.field_name) {
          if (!map[req.field_name]) {
            map[req.field_name] = req
          }
        }
      })
      return map
    },
    pendingRequestByField() {
      const map = {}
      this.profileRequests.forEach(req => {
        if (req.status === 'pending' && req.field_name) {
          map[req.field_name] = req
        }
      })
      return map
    },

    sensitiveStatusSummary() {
      // Top-of-tab unified status overview, derived from the already-loaded
      // profileRequests list (no extra API call needed).
      const pending = []
      const rejected = []
      const accepted = []
      this.profileRequests.forEach(req => {
        if (req.status === 'pending') pending.push(req)
        else if (req.status === 'rejected') rejected.push(req)
        else if (req.status === 'accepted') accepted.push(req)
      })
      // Stable-order newest first.
      pending.sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      rejected.sort((a, b) => ((a.processed_at || a.created_at) < (b.processed_at || b.created_at) ? 1 : -1))
      return {
        pending,
        rejected,
        acceptedCount: accepted.length,
      }
    },

    hasAnyPendingSensitiveRequest() {
      return this.sensitiveStatusSummary.pending.length > 0
    },

    recentChangeRequests() {
      // Last 5 requests ever submitted by this supplier, newest first.
      return [...this.profileRequests]
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
        .slice(0, 5)
    },
  },
  watch: {
    '$route.params.tab': {
      immediate: true,
      handler(newTab) {
        this.setActiveTabFromRoute(newTab)
      },
    },
  },
  created() {
    this.loadCurrentUser()
    if (this.isSupplier) {
      this.fetchPricing()
      this.fetchToken()
      this.loadProfileChangeRequests()
    }
    if (this.canManageSyncMode) {
      this.fetchSyncMode()
    }
  },
  methods: {
    setActiveTabFromRoute(tabName) {
      const validName = this.tabNameToIndex[tabName] !== undefined ? tabName : 'general'
      this.activeTabIndex = this.tabNameToIndex[validName]
    },
    onTabChanged(index) {
      const name = this.tabIndexToName[index]
      if (name && this.$route.params.tab !== name) {
        this.$router.replace({ name: 'profile', params: { tab: name } }).catch(() => {})
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },

    loadFromUser(user) {
      if (!user) return
      this.currentUserId = user.id || null
      this.company = {
        name: user.username || '',
        address: user.address || '',
        phone1: user.phone || '',
        phone2: user.phone2 || '',
        tax_identification_number: user.tax_identification_number != null ? String(user.tax_identification_number) : '',
        cin: user.cin || '',
        page_name: user.page_name || '',
        website_url: user.website_url || '',
      }
      // Payment info is read-only on the profile page
      this.paymentType = user.payment_type || ''
      this.rib = user.rib || ''
      this.invoiceDays = Array.isArray(user.invoice_generation_days) ? user.invoice_generation_days : []

      this.legalDraft = {
        cin: user.cin || '',
        tax_identification_number: user.tax_identification_number != null ? String(user.tax_identification_number) : '',
      }
      this.paymentDraft = {
        payment_type: user.payment_type || '',
        rib: user.rib || '',
        invoice_generation_days: Array.isArray(user.invoice_generation_days) ? [...user.invoice_generation_days] : [],
      }
    },

    async loadCurrentUser() {
      const storedUser = storeAuth.state.user
      if (storedUser && storedUser.id) {
        this.loadFromUser(storedUser)
      }
      try {
        const { data } = await axios.get('/api/v1/users/me/')
        this.loadFromUser(data)
        storeAuth.commit('setUser', data)
      } catch (error) {
        // Fallback to store only; do not block the page
      }
    },

    async fetchSyncMode() {
      try {
        const { data } = await axios.get('/api/companies/company/sync-mode/')
        this.syncMode = data.sync_mode || 'governorate'
        this.originalSyncMode = this.syncMode
        this.hasSyncs = data.has_syncs || false
        if (Array.isArray(data.available_modes) && data.available_modes.length) {
          this.syncModeOptions = data.available_modes.map(mode => ({
            value: mode.value,
            text: this.getSyncModeLabel(mode.value, mode.label),
          }))
        }
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement du mode de synchronisation')
      }
    },

    async saveSyncMode() {
      if (this.hasSyncs && this.syncMode !== this.originalSyncMode) {
        try {
          const { data } = await axios.get('/api/companies/all-syncs/')
          this.syncConfigCount = Array.isArray(data) ? data.length : (data?.results || []).length
        } catch (error) {
          this.syncConfigCount = 0
        }
        this.pendingSyncMode = this.syncMode
        this.confirmationText = ''
        if (this.$refs.syncConfirmModal) {
          this.$refs.syncConfirmModal.show()
        }
        return
      }

      await this.executeSyncModeChange()
    },

    cancelSyncModeChange() {
      this.syncMode = this.originalSyncMode
      this.confirmationText = ''
      this.pendingSyncMode = null
      if (this.$refs.syncConfirmModal) {
        this.$refs.syncConfirmModal.hide()
      }
    },

    async confirmSyncModeChange() {
      if (this.confirmationText !== 'SUPPRIMER') return
      if (this.$refs.syncConfirmModal) {
        this.$refs.syncConfirmModal.hide()
      }
      await this.executeSyncModeChange()
    },

    async executeSyncModeChange() {
      this.isSavingSyncMode = true
      try {
        const { data } = await axios.post('/api/companies/company/sync-mode/', {
          sync_mode: this.syncMode,
        })
        this.originalSyncMode = this.syncMode
        this.hasSyncs = data.has_syncs || false
        this.confirmationText = ''
        this.pendingSyncMode = null
        this.showToast('success', 'top-center', 'Mode de synchronisation mis à jour')
      } catch (error) {
        this.syncMode = this.originalSyncMode
        this.showToast('danger', 'top-center', 'Erreur lors de la mise à jour du mode de synchronisation')
      } finally {
        this.isSavingSyncMode = false
      }
    },

    getSyncModeLabel(mode, fallback = null) {
      const labels = {
        supplier: 'Par fournisseur',
        governorate: 'Par gouvernorat',
        supplier_governorate: 'Par fournisseur et gouvernorat',
      }
      return labels[mode] || fallback || mode
    },

    async changePassword() {
      const isValid = await this.$refs?.passwordRules?.validate()
      if (!isValid) return

      this.isSubmittingPassword = true
      try {
        await axios.post('/api/auth/change-password/', {
          old_password: this.password.old_password,
          new_password: this.password.password,
          confirm_password: this.password.confirm_password,
        })
        this.showToast('success', 'top-center', 'Mot de passe mis à jour avec succès')
        await storeAuth.dispatch('userLogout')
        axios.defaults.headers.common.Authorization = ''
        this.$router.replace({ name: 'auth' }).catch(() => {})
        this.password = {
          old_password: '',
          password: '',
          confirm_password: '',
        }
        this.$nextTick(() => {
          if (this.$refs.passwordRules) {
            this.$refs.passwordRules.reset()
          }
        })
      } catch (error) {
        const responseErrors = error?.response?.data
        let message = 'Erreur lors de la mise à jour du mot de passe'
        if (responseErrors) {
          const messages = []
          Object.keys(responseErrors).forEach(key => {
            const value = responseErrors[key]
            if (Array.isArray(value)) messages.push(...value)
            else messages.push(String(value))
          })
          if (messages.length) message = messages.join(' / ')
        }
        this.showToast('danger', 'top-center', message)
      } finally {
        this.isSubmittingPassword = false
      }
    },

    async save() {
      const isValid = await this.$refs.simpleRules.validate()
      if (!isValid) return

      if (!this.currentUserId) {
        this.showToast('danger', 'top-center', 'Utilisateur non identifié')
        return
      }

      this.isSaving = true

      const payload = {
        username: this.company.name,
        address: this.company.address,
        phone: this.company.phone1,
        phone2: this.company.phone2,
        page_name: this.company.page_name,
        website_url: this.company.website_url,
      }

      try {
        const { data } = await axios.put(`/api/auth/users/update/${this.currentUserId}/`, payload)
        this.showToast('success', 'top-center', 'Vos données ont été modifiées avec succès')
        this.loadFromUser(data)
        storeAuth.commit('setUser', data)
      } catch (error) {
        const responseErrors = error?.response?.data
        let message = 'Erreur lors de la mise à jour du profil'
        if (responseErrors) {
          const messages = []
          Object.keys(responseErrors).forEach(key => {
            const value = responseErrors[key]
            if (Array.isArray(value)) messages.push(...value)
            else messages.push(String(value))
          })
          if (messages.length) message = messages.join(' / ')
        }
        this.showToast('danger', 'top-center', message)
      } finally {
        this.isSaving = false
      }
    },

    formatPhone(value) {
      return String(value).replace(/[^\d]/g, '').slice(0, 8)
    },

    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    },

    formatPrice(value) {
      if (value == null || value === '') return 'Non configuré'
      const num = Number(value)
      if (Number.isNaN(num)) return 'Non configuré'
      return `${num.toFixed(3)} TND`
    },

    // Profile change request methods
    getFieldLabel(field) {
      const labels = {
        cin: 'CIN',
        tax_identification_number: 'Matricule fiscale',
        payment_type: 'Type de paiement',
        rib: 'RIB',
        invoice_generation_days: 'Jours de paiement préférés',
      }
      return labels[field] || field
    },

    getFieldDisplayValue(field) {
      if (field === 'payment_type') return this.getPaymentTypeLabel(this.paymentType)
      if (field === 'invoice_generation_days') {
        if (!this.invoiceDays.length) return '—'
        return this.invoiceDays.map(d => this.getPaymentDayLabel(d)).join(', ')
      }
      return this.company[field] || '—'
    },

    onCinInput(value) {
      // Strip non-digit characters and cap at 8 digits so the input only
      // ever holds the allowed CIN vocabulary while typing.
      const digits = String(value == null ? '' : value).replace(/\D/g, '').slice(0, 8)
      this.$nextTick(() => {
        if (digits !== value) this.legalDraft.cin = digits
      })
    },

    isCinValid() {
      // Empty is allowed (supplier may have only tax id). When present, must be 8 digits.
      const cin = this.legalDraft.cin || ''
      return cin === '' || /^\d{8}$/.test(cin)
    },

    onRibInput(value) {
      // Mirror of onCinInput: strip non-digits and cap at 20 (the Tunisian RIB length).
      const digits = String(value == null ? '' : value).replace(/\D/g, '').slice(0, 20)
      this.$nextTick(() => {
        if (digits !== value) this.paymentDraft.rib = digits
      })
    },

    isRibValid() {
      // Empty RIB is allowed (e.g. when payment_type=espece). When present,
      // must be exactly 20 digits.
      const rib = this.paymentDraft.rib || ''
      return rib === '' || /^\d{20}$/.test(rib)
    },

    normalizeString(value) {
      // Treat null / undefined the same as an empty string so comparisons
      // don't miss a change just because the API returned null while the
      // draft became an empty string.
      if (value === null || value === undefined) return ''
      return String(value)
    },

    arraysEqual(a, b) {
      const left = Array.isArray(a) ? a : []
      const right = Array.isArray(b) ? b : []
      if (left.length !== right.length) return false
      const sortedLeft = [...left].sort()
      const sortedRight = [...right].sort()
      return sortedLeft.every((val, idx) => val === sortedRight[idx])
    },

    openBatchRequestModal() {
      // Global guard: the backend only allows one pending profile change
      // request per supplier at a time. Mirror that rule in the UI so the
      // supplier gets immediate, clear feedback.
      if (this.hasAnyPendingSensitiveRequest) {
        this.showToast(
          'warning',
          'top-center',
          "Vous avez déjà une demande en attente. Annulez-la ou attendez la validation de l'administrateur.",
        )
        return
      }

      // Scan ALL sensitive fields across the two sub-groups (Légal + Paiement)
      // and queue whichever ones were actually modified. Validate the
      // numeric-format ones (CIN / RIB) before queuing; abort with a toast
      // if any fails so the modal never opens with bad data.
      const changes = []

      // --- Légal sub-group ---
      const currentCin = this.normalizeString(this.legalDraft.cin)
      const originalCin = this.normalizeString(this.company.cin)
      const hasCinChange = currentCin !== originalCin
      if (hasCinChange && !this.isCinValid()) {
        this.showToast('warning', 'top-center', 'Le CIN doit contenir exactement 8 chiffres.')
        return
      }
      if (hasCinChange) {
        changes.push({
          field: 'cin', label: 'CIN', old: originalCin || '—', new: currentCin,
        })
      }

      const currentTax = this.normalizeString(this.legalDraft.tax_identification_number)
      const originalTax = this.normalizeString(this.company.tax_identification_number)
      if (currentTax !== originalTax) {
        changes.push({
          field: 'tax_identification_number',
          label: 'Matricule fiscale',
          old: originalTax || '—',
          new: currentTax,
        })
      }

      // --- Paiement sub-group ---
      const currentPaymentType = this.normalizeString(this.paymentDraft.payment_type)
      const originalPaymentType = this.normalizeString(this.paymentType)
      if (currentPaymentType !== originalPaymentType) {
        changes.push({
          field: 'payment_type',
          label: 'Type de paiement',
          old: this.getPaymentTypeLabel(originalPaymentType) || '—',
          new: currentPaymentType,
          newLabel: this.getPaymentTypeLabel(currentPaymentType),
        })
      }

      const currentRib = this.normalizeString(this.paymentDraft.rib)
      const originalRib = this.normalizeString(this.rib)
      const ribChanged = currentRib !== originalRib
      if (ribChanged && !this.isRibValid()) {
        this.showToast('warning', 'top-center', 'Le RIB doit contenir exactement 20 chiffres.')
        return
      }
      if (ribChanged) {
        changes.push({
          field: 'rib', label: 'RIB', old: originalRib || '—', new: currentRib,
        })
      }

      const daysChanged = !this.arraysEqual(
        this.invoiceDays,
        this.paymentDraft.invoice_generation_days,
      )
      if (daysChanged) {
        const oldLabel = this.invoiceDays.map(d => this.getPaymentDayLabel(d)).join(', ') || 'Aucun'
        const newLabel = this.paymentDraft.invoice_generation_days.map(d => this.getPaymentDayLabel(d)).join(', ') || 'Aucun'
        changes.push({
          field: 'invoice_generation_days',
          label: 'Jours de paiement',
          old: oldLabel,
          new: [...this.paymentDraft.invoice_generation_days],
          newLabel,
        })
      }

      if (changes.length === 0) {
        this.showToast('info', 'top-center', 'Aucune modification détectée.')
        return
      }

      this.batchRequestModal = {
        show: true,
        isSubmitting: false,
        section: '',
        changes,
      }
    },

    formatSensitiveValue(value, fieldName) {
      // Translate raw stored values into human-readable labels for the
      // status banner / pills / history list.
      if (value === null || value === undefined || value === '') return '—'
      if (fieldName === 'invoice_generation_days') {
        if (!Array.isArray(value) || !value.length) return 'Aucun'
        return value.map(d => this.getPaymentDayLabel(d)).join(', ')
      }
      if (fieldName === 'payment_type') return this.getPaymentTypeLabel(value)
      if (Array.isArray(value)) return value.join(', ')
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },

    openRejectedDetail(req) {
      this.rejectedDetailModal = { show: true, req: req || {} }
    },

    async cancelPendingRequest(requestId) {
      if (!requestId) return
      const ok = await this.$bvModal.msgBoxConfirm(
        'Voulez-vous vraiment annuler cette demande ? Elle sera définitivement retirée et ne pourra plus être traitée par l\'administrateur.',
        {
          title: 'Annuler ma demande',
          okTitle: 'Annuler la demande',
          cancelTitle: 'Retour',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!ok) return
      try {
        await axios.delete(`/api/auth/profile-change-requests/${requestId}/cancel/`)
        this.showToast('success', 'top-center', 'Demande annulée.')
        this.loadProfileChangeRequests()
      } catch (error) {
        const detail = error?.response?.data?.detail
        let message = 'Erreur lors de l\'annulation de la demande'
        if (detail) message = Array.isArray(detail) ? detail.join(' / ') : String(detail)
        this.showToast('danger', 'top-center', message)
        // Always refresh so a stale-state chip clears (e.g. if the row was
        // accepted/rejected in another tab since the supplier opened the page).
        this.loadProfileChangeRequests()
      }
    },

    async submitBatchRequests() {
      this.batchRequestModal.isSubmitting = true
      // Use Promise.allSettle so a single failing field doesn't mask the
      // successes nor break the whole batch. Report each outcome honestly.
      const results = await Promise.allSettled(
        this.batchRequestModal.changes.map(change => axios.post('/api/auth/profile-change-requests/', {
          field_name: change.field,
          new_value: change.new,
        })),
      )
      const succeeded = []
      const failed = []
      results.forEach((res, idx) => {
        const label = this.getFieldLabel(this.batchRequestModal.changes[idx].field)
        if (res.status === 'fulfilled') {
          succeeded.push(label)
        } else {
          const detail = res.reason?.response?.data
          let msg = 'Erreur inconnue'
          if (detail) {
            if (typeof detail === 'string') msg = detail
            else if (detail.detail) msg = Array.isArray(detail.detail) ? detail.detail.join(' / ') : String(detail.detail)
            else {
              const parts = []
              Object.keys(detail).forEach(k => {
                const v = detail[k]
                parts.push(Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${v}`)
              })
              msg = parts.join(' / ') || msg
            }
          }
          failed.push(`${label} (${msg})`)
        }
      })

      if (failed.length === 0) {
        this.showToast('success', 'top-center', 'Vos demandes ont été soumises avec succès.')
        this.batchRequestModal.show = false
      } else if (succeeded.length === 0) {
        this.showToast('danger', 'top-center', `Échec de la soumission : ${failed.join(' / ')}`)
      } else {
        this.showToast('warning', 'top-center', `${succeeded.length} demande(s) envoyée(s). Échec : ${failed.join(' / ')}`)
        this.batchRequestModal.show = false
      }
      this.loadProfileChangeRequests()
      this.batchRequestModal.isSubmitting = false
    },
    openRequestModal(field) {
      let fieldType = 'text'
      let currentValue = this.company[field] || ''
      if (field === 'payment_type') {
        fieldType = 'select'
        currentValue = this.paymentType
      } else if (field === 'invoice_generation_days') {
        fieldType = 'checkbox'
        currentValue = [...this.invoiceDays]
      }
      this.requestModal = {
        show: true,
        isSubmitting: false,
        field,
        fieldLabel: this.getFieldLabel(field),
        fieldType,
        currentValue,
        newValue: field === 'invoice_generation_days' ? [] : '',
        options: field === 'payment_type'
          ? [{ value: 'espece', text: 'Espèce' }, { value: 'virement', text: 'Virement' }]
          : this.paymentDayOptions,
      }
    },

    closeRequestModal() {
      this.requestModal.show = false
    },

    async submitProfileChangeRequest() {
      const { field, newValue, fieldType } = this.requestModal
      if (fieldType === 'text' && !String(newValue).trim()) {
        this.showToast('warning', 'top-center', 'Veuillez saisir une nouvelle valeur.')
        return
      }
      this.requestModal.isSubmitting = true
      try {
        await axios.post('/api/auth/profile-change-requests/', {
          field_name: field,
          new_value: newValue,
        })
        this.showToast('success', 'top-center', 'Demande envoyée avec succès.')
        this.closeRequestModal()
        this.loadProfileChangeRequests()
      } catch (error) {
        const messages = []
        const data = error?.response?.data
        if (data) {
          Object.keys(data).forEach(key => {
            const value = data[key]
            if (Array.isArray(value)) messages.push(...value)
            else messages.push(String(value))
          })
        }
        this.showToast('danger', 'top-center', messages.length ? messages.join(' / ') : 'Erreur lors de l\'envoi de la demande')
      } finally {
        this.requestModal.isSubmitting = false
      }
    },

    async loadProfileChangeRequests() {
      if (!this.isSupplier) return
      this.isLoadingRequests = true
      try {
        const { data } = await axios.get('/api/auth/profile-change-requests/')
        this.profileRequests = Array.isArray(data) ? data : data.results || []
      } catch (error) {
        // Fail silently; this list is non-blocking
      } finally {
        this.isLoadingRequests = false
      }
    },

    getRequestStatusVariant(status) {
      const map = {
        pending: 'warning',
        accepted: 'success',
        rejected: 'danger',
      }
      return map[status] || 'secondary'
    },

    // Pricing methods
    async fetchPricing() {
      this.isLoadingPricing = true
      try {
        const { data } = await axios.get('/api/auth/users/suppliers/me/pricing/')
        this.pricing = {
          price_delivery: data.price_delivery,
          price_return: data.price_return,
          prices: data.prices || [],
          weight_price: data.weight_price,
          governorate_prices: data.governorate_prices || [],
          pricing_mode: data.pricing_mode || 'fixed',
          weights: data.weights || [],
        }
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des tarifs')
      } finally {
        this.isLoadingPricing = false
      }
    },

    getPricingModeLabel(mode) {
      const modes = {
        fixed: 'Prix unique par colis',
        weight: 'Par poids',
        governorate: 'Par gouvernorat',
      }
      return modes[mode] || mode
    },

    getPaymentTypeLabel(type) {
      const labels = {
        espece: 'Espèce',
        virement: 'Virement',
      }
      return labels[type] || type || '—'
    },

    getPaymentDayLabel(day) {
      const labels = {
        monday: 'Lundi',
        tuesday: 'Mardi',
        wednesday: 'Mercredi',
        thursday: 'Jeudi',
        friday: 'Vendredi',
        saturday: 'Samedi',
        sunday: 'Dimanche',
      }
      return labels[day] || day
    },

    // API token methods
    async fetchToken() {
      this.isLoadingToken = true
      this.tokenError = false
      try {
        // The user's session token is the API token; show it directly
        const storedToken = localStorage.getItem('token')
        if (storedToken) {
          this.token = storedToken
          this.isLoadingToken = false
          return
        }
        // Fallback to the backend endpoint if no token in storage
        const { data } = await axios.get('/api/auth/token/')
        this.token = data.token || ''
      } catch (error) {
        this.token = ''
        this.tokenError = true
      } finally {
        this.isLoadingToken = false
      }
    },

    async copyToken() {
      if (!this.token) return
      try {
        await navigator.clipboard.writeText(this.token)
        this.showToast('success', 'top-center', 'Token copié dans le presse-papiers')
      } catch (error) {
        this.showToast('danger', 'top-center', 'Impossible de copier le token')
      }
    },
  },

}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/quill.scss';
@import '@core/scss/vue/pages/page-blog.scss';

// Redesigned Sync Mode Confirmation Modal Styles
.sync-confirm-modal {
  .top-icon-container {
    background: white;
    padding: 0.5rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    position: relative;

    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: rgba(234, 84, 85, 0.4);
      animation: ripple 2s infinite;
      z-index: 0;
    }

    .top-icon-inner {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(234, 84, 85, 0.1);
      border: 2px solid #ea5455;
      z-index: 1;
    }
  }

  @keyframes ripple {
    0% {
      transform: scale(1);
      opacity: 0.6;
    }
    100% {
      transform: scale(1.8);
      opacity: 0;
    }
  }

  .rounded-xl {
    border-radius: 2rem !important;
  }

  .danger-zone-box {
    background-color: #fff8f8;
    border-left: 4px solid #ea5455;
    padding: 1.2rem;
    border-radius: 0.8rem;
  }

  .x-small {
    font-size: 0.7rem;
    letter-spacing: 0.05rem;
  }

  .confirmation-input-new {
    border-radius: 1rem;
    background-color: #f8fafd;
    border: 2px solid #edf2f9;
    color: #b1c2d9;
    font-weight: 700;
    transition: all 0.3s ease;

    &::placeholder {
      color: #d1d9e6;
    }

    &:focus {
      border-color: #ea5455;
      background-color: white;
      color: #ea5455;
      box-shadow: 0 5px 15px rgba(234, 84, 85, 0.1);
    }
  }

  .btn-cancel {
    background-color: #f1f4f8;
    color: #4b5a6d;
    border: none;
    border-radius: 1rem;
    font-weight: 600;

    &:hover {
      background-color: #e2e8f0;
    }
  }

  .btn-confirm {
    border-radius: 1rem;
    font-weight: 600;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .shadow-danger {
    box-shadow: 0 8px 20px rgba(234, 84, 85, 0.3) !important;
  }

  // Pills/badges that act as buttons get a pointer cursor and a hover affordance.
  .cursor-pointer {
    cursor: pointer;
  }
  .cursor-pointer:hover {
    opacity: 0.85;
  }

  // Truncate the "Motif" column on the recent demands list so long admin
  // responses don't blow out the table width on small screens.
  .admin-response-cell {
    display: inline-block;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }

  // Disabled state styling for submit buttons
  // (same "not-allowed" pattern used on the Create Roadmap page).
  .btn.not-allowed,
  .btn[disabled],
  .submit-sensitive-btn.not-allowed,
  .submit-sensitive-btn[disabled] {
    cursor: not-allowed !important;
    opacity: 0.6 !important;
    pointer-events: auto !important;
  }
}
  /* Premium UI/UX Customizations */
  .profile-page {
    animation: fadeIn 0.5s ease-in-out;

    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .profile-card {
      border-radius: 16px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05) !important;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      background: rgba(255, 255, 255, 0.95);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(0, 0, 0, 0.02) !important;

      &:hover {
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08) !important;
      }
    }

    .submit-sensitive-btn {
      background: linear-gradient(135deg, #C62828 0%, #E53935 100%);
      border: none;
      box-shadow: 0 4px 15px rgba(198, 40, 40, 0.4);
      transition: all 0.3s ease;
      border-radius: 8px;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(198, 40, 40, 0.6);
      }
    }

    .nav-pills .nav-link.active {
      background: linear-gradient(135deg, #C62828 0%, #E53935 100%) !important;
      box-shadow: 0 4px 15px rgba(198, 40, 40, 0.3) !important;
      border-radius: 8px !important;
      transition: all 0.3s ease;
    }

    /* Form Labels */
    .col-form-label, label {
      font-size: 0.85rem !important;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 700;
      color: #4b5a6d;
      margin-bottom: 0.4rem;
    }

    /* Input fields */
    .form-control {
      border-radius: 8px;
      padding: 0.75rem 1rem;
      height: auto;
      font-size: 0.95rem;
      transition: all 0.3s ease;
      border-color: #e2e8f0;

      &:focus {
        box-shadow: 0 0 0 0.2rem rgba(198, 40, 40, 0.25) !important;
        border-color: #C62828 !important;
      }

      &::placeholder {
        color: #cbd5e1;
        font-weight: 400;
      }
    }

    /* Input group icons */
    .input-group-text {
      background-color: #f2f7fc !important;
      border-color: #e2e8f0;
      color: #C62828;
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
      padding: 0.75rem 1rem;
    }
    .input-group-merge .form-control:focus + .input-group-text,
    .input-group-merge .input-group-text:has(+ .form-control:focus) {
      border-color: #C62828;
    }

    /* Premium Pending Request Flag */
    .premium-status-flag {
      background: rgba(255, 159, 67, 0.06);
      border: 1px solid rgba(255, 159, 67, 0.15);
      border-left: 4px solid #FF9F43;
      border-radius: 12px;
      padding: 1.25rem 1.5rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-shadow: 0 4px 15px rgba(255, 159, 67, 0.05);
      margin-bottom: 2rem;

      .flag-title {
        color: #cc7414;
        font-size: 1.05rem;
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        /* Pulsing indicator */
        &::before {
          content: '';
          display: block;
          width: 8px;
          height: 8px;
          background-color: #FF9F43;
          border-radius: 50%;
          box-shadow: 0 0 0 0 rgba(255, 159, 67, 0.7);
          animation: pulse-orange 2s infinite;
        }
      }

      .flag-detail-badge {
        background: white;
        border: 1px solid rgba(255, 159, 67, 0.3);
        color: #e07f15;
        padding: 0.35rem 0.75rem;
        border-radius: 6px;
        font-weight: 600;
        font-size: 0.85rem;
        display: inline-block;
        margin-top: 0.75rem;
        box-shadow: 0 2px 5px rgba(0,0,0,0.02);
      }

      .flag-description {
        color: #82868b;
        font-size: 0.85rem;
        margin-top: 0.5rem;
      }

      .flag-action-btn {
        border-radius: 20px;
        font-weight: 600;
        letter-spacing: 0.3px;
        transition: all 0.2s ease;

        &:hover {
          transform: scale(1.02);
        }
      }
    }

    @keyframes pulse-orange {
      0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 159, 67, 0.7); }
      70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(255, 159, 67, 0); }
      100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 159, 67, 0); }
    }

    /* Premium Checkbox Group */
    .premium-checkbox-group {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 1.25rem;
      transition: border-color 0.3s ease;

      &:hover {
        border-color: #cbd5e1;
      }

      .custom-checkbox-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 1.25rem 2rem;
      }

      /* Redesign checkbox colors to Brand Red */
      .custom-control-input:checked ~ .custom-control-label::before {
        border-color: #C62828 !important;
        background-color: #C62828 !important;
      }
      .custom-control-input:focus ~ .custom-control-label::before {
        box-shadow: 0 0 0 0.2rem rgba(198, 40, 40, 0.25) !important;
      }
    }

    /* Premium Metric Cards */
    .premium-metric-card {
      background: #ffffff;
      border: 1px solid #f1f5f9;
      border-radius: 12px;
      padding: 1.5rem;
      display: flex;
      align-items: center;
      gap: 1.25rem;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
      transition: all 0.3s ease;
      margin-bottom: 1rem;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(198, 40, 40, 0.08);
        border-color: rgba(198, 40, 40, 0.1);
      }

      .metric-icon-wrapper {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background-color: #f2f7fc;
        color: #C62828;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .metric-content {
        flex-grow: 1;

        .metric-label {
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.75rem;
          display: block;
          margin-bottom: 0.25rem;
        }

        .metric-value {
          color: #C62828;
          font-weight: 700;
          margin-bottom: 0;
          font-size: 1.25rem;
        }
      }
    }

    /* Premium Tables */
    .premium-table-wrapper {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
      background: #ffffff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.02);
    }

    .premium-requests-table {
      margin-bottom: 0;

      th {
        background-color: #f8fafc !important;
        color: #64748b;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 1rem 1.25rem;
        border-top: none;
        border-bottom: 1px solid #e2e8f0;
      }

      td {
        padding: 1rem 1.25rem;
        vertical-align: middle;
        border-top: 1px solid #f1f5f9;
        color: #334155;
        font-weight: 500;
      }

      tbody tr {
        transition: all 0.2s ease;

        &:hover {
          background-color: #f2f7fc;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          position: relative;
          z-index: 1;
        }
      }
    }

    /* Premium Token Container */
    .premium-token-container {
      background: #ffffff;
      border: 1px solid #f1f5f9;
      border-radius: 12px;
      padding: 1.5rem;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);

      .premium-label {
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-size: 0.75rem;
        color: #64748b;
        margin-bottom: 0.75rem;
        display: block;
      }

      .premium-token-input-group {
        box-shadow: 0 2px 4px rgba(0,0,0,0.02);
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #e2e8f0;
        transition: all 0.3s ease;

        &:focus-within {
          border-color: #C62828;
          box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.1);
        }

        .premium-token-input {
          border: none !important;
          background: #f8fafc !important;
          font-family: 'Courier New', Courier, monospace;
          font-size: 1rem;
          color: #334155;
          letter-spacing: 1px;
          padding: 1rem 1.25rem;
          height: auto;

          &:focus {
            box-shadow: none !important;
            background: #ffffff !important;
            outline: none !important;
          }
        }

        .token-action-btn {
          border: none !important;
          background: #f8fafc !important;
          color: #64748b !important;
          border-left: 1px solid #e2e8f0 !important;
          padding: 0 1.25rem;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          border-radius: 0 !important;

          &:hover {
            background: #e2e8f0 !important;
            color: #334155 !important;
          }

          &.copy-btn {
            color: #C62828 !important;
            background: #f2f7fc !important;

            &:hover {
              background: #C62828 !important;
              color: #ffffff !important;
            }

            &:disabled {
              opacity: 0.6;
              background: #f8fafc !important;
              color: #94a3b8 !important;
            }
          }
        }
      }

      .security-hint {
        color: #94a3b8;
        font-weight: 500;
      }
    }
  }
</style>
